// src/components/AuthorProfileLayout.js
import React from 'react';
import { SiFacebook, SiInstagram, SiLinkedin } from 'react-icons/si';
import ArticleCard from './ArticleCard';
import { IoArrowBack, IoArrowForward } from 'react-icons/io5';
import heroBg from '../assets/images/hero-background.png';

const getSocialIcon = (url) => {
    if (url.includes('facebook.com')) return <SiFacebook />;
    if (url.includes('instagram.com')) return <SiInstagram />;
    if (url.includes('linkedin.com')) return <SiLinkedin />;
    return null;
};

// ✅ ADD A LOADING SPINNER COMPONENT
const Loader = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-dark-bg z-50">
    <div className="w-16 h-16 border-4 border-dark-accent-start border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const AuthorProfileLayout = ({ author, posts, pagination, loading }) => { // ✅ ADD loading PROP
    const { currentPage, totalPages, handlePrevPage, handleNextPage } = pagination;

    // ✅ SHOW LOADER IF LOADING
    if (loading) {
        return <Loader />;
    }
    
    // Fallback if author data is somehow missing after loading
    if (!author) return <div className="text-center py-48 text-red-400">Author not found.</div>;

    return (
        <div className="relative isolate">
            <section className="relative h-screen flex flex-col items-center justify-center text-center overflow-hidden px-6">
                <div className="hero-bg-image-container">
                    <img src={heroBg} alt="Abstract background" className="w-full h-full object-cover animate-pan-zoom" />
                </div>
                <div className="relative z-10">
                    <img src={author.avatar_urls['96']} alt={author.name} className="h-24 w-24 rounded-full mx-auto ring-4 ring-white/10" />
                    <h1 className="mt-6 text-4xl font-bold tracking-tight text-dark-text-primary sm:text-5xl">
                        {author.name}
                    </h1>
                    <p className="mt-4 max-w-2xl mx-auto text-lg text-dark-text-secondary">{author.description}</p>
                    
                    {author.yoast_head_json?.schema?.['@graph']?.find(item => item['@type'] === 'Person')?.sameAs && (
                        <div className="mt-6 flex justify-center gap-x-6">
                            {author.yoast_head_json.schema['@graph'].find(item => item['@type'] === 'Person').sameAs.map(url => {
                                const icon = getSocialIcon(url);
                                if (icon && !url.includes('blogs.oolook.in')) {
                                    return (
                                        <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="text-dark-text-secondary/60 hover:text-white transition-colors">
                                            {React.cloneElement(icon, { size: 20 })}
                                        </a>
                                    );
                                }
                                return null;
                            })}
                        </div>
                    )}
                </div>
            </section>
            
            <section className="py-24 sm:py-32">
                 <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-dark-text-primary text-center">Articles by {author.name}</h2>
                    {posts.length > 0 ? (
                        <>
                            <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-3">
                                {posts.map(post => <ArticleCard key={post.id} post={post} />)}
                            </div>

                            {totalPages > 1 && (
                                <div className="mt-16 flex justify-center items-center gap-4">
                                    <button onClick={handlePrevPage} disabled={currentPage === 1} className="flex items-center gap-2 rounded-md bg-white/10 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-white/20 disabled:opacity-50">
                                        <IoArrowBack /> Previous
                                    </button>
                                    <span className="text-sm text-dark-text-secondary">Page {currentPage} of {totalPages}</span>
                                    <button onClick={handleNextPage} disabled={currentPage === totalPages} className="flex items-center gap-2 rounded-md bg-white/10 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-white/20 disabled:opacity-50">
                                        Next <IoArrowForward />
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <p className="mt-12 text-center text-dark-text-secondary">No articles found for this author.</p>
                    )}
                </div>
            </section>
        </div>
    );
};

export default AuthorProfileLayout;